import type { SiteMapResponseSchema } from '@inkitt/contracts-gweb';
import type { StoryData } from '../services/api/goApi';
import type { CategoryPaths } from './categoryPaths';

type StoryTag = NonNullable<StoryData['grouped_tags']>[number];
export type MatchedTag = StoryTag & {
  nrrWebId?: number;
  /** The category page's verified canonical path; absent while unverified or when it 404s. */
  categoryPath?: string;
};

/** tag_slug → shelf_web_id — the only two sitemap fields tag matching reads. */
export type NrrShelfWebIds = Record<string, number>;

/**
 * Everything passed to a `'use client'` component is serialized into the RSC payload —
 * the raw shelves sitemap was ~445 KB of every story/list document (DEV-5890). Project
 * `tag_kinds` down to the two fields `matchTagsWithNRR` reads before crossing the
 * server→client boundary.
 */
export function toNrrShelfWebIds(tagKinds: SiteMapResponseSchema['tag_kinds'] = []): NrrShelfWebIds {
  // Accumulate on a null-prototype object so an adversarial/odd tag_slug like
  // "__proto__" is stored as a plain data key instead of hitting the prototype setter.
  const webIdsBySlug: NrrShelfWebIds = Object.create(null);

  tagKinds.forEach((tagKind) => {
    tagKind.shelves.forEach((shelf) => {
      webIdsBySlug[shelf.tag_slug] = shelf.shelf_web_id;
    });
  });

  // Spread back onto a plain-prototype object before returning: React's flight
  // serializer THROWS on null-prototype props ("Classes or null prototypes are not
  // supported"), and spread uses define-semantics, so a "__proto__" key survives
  // as an own property.
  return { ...webIdsBySlug };
}

/** The unique NRR shelf web ids behind these tag slugs, to resolve with getCategoryPathsWithin. */
export function toNrrWebIds(tagSlugs: string[], nrrShelfWebIds: NrrShelfWebIds): number[] {
  const webIds = tagSlugs.flatMap((slug) => {
    const webId = nrrShelfWebIds[slug];
    // Same typeof guard as matchTagsWithNRR: inherited members are never numbers.
    return typeof webId === 'number' ? [webId] : [];
  });
  return [...new Set(webIds)];
}

export function matchTagsWithNRR(
  storyTags: StoryTag[] = [],
  nrrShelfWebIds: NrrShelfWebIds = {},
  categoryPaths: CategoryPaths = {},
): MatchedTag[] {
  const matchedTags: MatchedTag[] = [];

  storyTags.forEach((storyTag) => {
    const nrrWebId = nrrShelfWebIds[storyTag.slug];
    // typeof-guard rather than a bare undefined check: inherited Object.prototype
    // members (toString, constructor, …) are all non-numbers, so a story tag slug
    // colliding with them can never produce a false match.
    if (typeof nrrWebId === 'number') {
      const categoryPath = categoryPaths[nrrWebId];
      matchedTags.push({
        ...storyTag,
        nrrWebId,
        ...(categoryPath && { categoryPath }),
      });
    } else if (storyTag.custom_image) {
      // Partner tags carrying a custom image (e.g. Harlequin) aren't always wired
      // into the NRR site map; surface them anyway so the badge still renders.
      matchedTags.push({ ...storyTag });
    }
  });

  return matchedTags;
}
